import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Brain, Battery, Users, Sparkles, Sun, Moon, Leaf } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const BenefitsOfYogaRetreats = () => {
  usePageMeta({
    title: "Benefits of Yoga Retreats | Why Go on a Yoga Retreat",
    description: "Discover the transformative benefits of yoga retreats: deep rest, stress relief, mental clarity, physical renewal, and lasting wellbeing.",
    canonical: "https://annelielily.com/retreats/benefits"
  });

  const mainBenefits = [
    {
      icon: Battery,
      title: "Deep Rest & Restoration",
      description: "A retreat gives you permission to truly rest — not just sleep, but the kind of deep restoration that recharges you at a cellular level. Away from screens, schedules, and responsibilities, your nervous system finally gets to downshift.",
      details: [
        "Complete break from daily stressors and demands",
        "Multiple restorative practices throughout each day",
        "Permission to do nothing without guilt",
        "Better quality sleep in a peaceful environment"
      ]
    },
    {
      icon: Brain,
      title: "Mental Clarity & Focus",
      description: "When the noise of everyday life quietens, clarity emerges. Retreats create the space for insights, decisions, and perspectives that get crowded out in busy life. Many guests report leaving with greater mental clarity than they've had in years.",
      details: [
        "Distance from daily problems brings new perspectives",
        "Meditation practices sharpen focus and awareness",
        "Space to think without constant interruptions",
        "Return home with clearer priorities"
      ]
    },
    {
      icon: Heart,
      title: "Emotional Release & Healing",
      description: "Yoga, breathwork, and sound healing can unlock emotions stored in the body. In a safe, supported environment, many people experience profound emotional release — tears, laughter, or simply a lightening of the heart.",
      details: [
        "Safe space to feel and process emotions",
        "Practices designed to release stored tension",
        "Supportive community witnessing your journey",
        "Opportunity for self-reflection and growth"
      ]
    },
    {
      icon: Sparkles,
      title: "Physical Renewal",
      description: "Multiple yoga sessions, nourishing food, fresh air, and quality rest work together to rejuvenate your body. Stiffness eases, energy returns, and you'll likely leave feeling years younger.",
      details: [
        "Improved flexibility and reduced tension",
        "Better digestion from healthy eating",
        "Increased energy and vitality",
        "Deeper awareness of your body's needs"
      ]
    },
    {
      icon: Users,
      title: "Connection & Community",
      description: "There's something special about sharing a retreat experience with others. Bonds form naturally over shared meals and practices. Many retreat friendships last long after the retreat ends.",
      details: [
        "Meet like-minded people on similar paths",
        "Shared experiences create natural connections",
        "Feel part of something bigger than yourself",
        "Support system that extends beyond the retreat"
      ]
    },
    {
      icon: Leaf,
      title: "New Habits & Inspiration",
      description: "Retreats can spark lasting change. Experiencing daily yoga, meditation, and healthy eating shows you what's possible. Many guests return home inspired to maintain new practices.",
      details: [
        "Learn new practices you can continue at home",
        "Experience what consistent self-care feels like",
        "Motivation to maintain healthier habits",
        "Fresh perspective on what matters most"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Benefits of Retreats" currentSlug="retreats/benefits" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Benefits of Yoga Retreats
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Why do thousands of people go on yoga retreats each year? Because the benefits 
              go far beyond what a single yoga class can offer. Discover how a retreat can 
              transform your body, mind, and spirit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why a Retreat Is Different from Regular Yoga Classes
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Weekly yoga classes are wonderful, but they can only do so much when you're 
                rushing from work, checking your phone, and thinking about your to-do list. 
                The moment you leave the studio, everyday stress floods back in.
              </p>
              <p>
                A yoga retreat is different. <strong>It's total immersion</strong> — hours or 
                days dedicated solely to your wellbeing, away from the demands and distractions 
                of normal life. This extended, focused time allows changes that simply can't 
                happen in a weekly class.
              </p>
              <p>
                Think of it like this: a yoga class is a sip of water. A yoga retreat is a 
                long, cool drink that actually quenches your thirst.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              The Transformative Benefits of Yoga Retreats
            </h2>
          </motion.div>

          <div className="space-y-8">
            {mainBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center">
                      <benefit.icon className="w-7 h-7 text-gold" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-heading text-xl text-neutral-800 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-neutral-700 mb-4 leading-relaxed">
                      {benefit.description}
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-2">
                      {benefit.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
                          <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Science Section */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              The Science Behind Retreat Benefits
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                The benefits of yoga retreats aren't just anecdotal — they're backed by research. 
                Studies have shown that immersive yoga experiences can:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Reduce cortisol levels</strong> — the stress hormone drops significantly during and after retreat experiences</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Improve heart rate variability</strong> — a key indicator of nervous system health and stress resilience</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Decrease anxiety and depression symptoms</strong> — effects that can last weeks or months after the retreat</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Enhance immune function</strong> — deep rest and reduced stress allow your body to repair and protect itself</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Improve sleep quality</strong> — effects that often continue long after returning home</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Benefits Most */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Who Benefits Most from Yoga Retreats?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-3">You're Running on Empty</h3>
                <p className="text-neutral-600 text-sm">
                  If you're exhausted, burnt out, or feel like you're constantly giving without 
                  receiving, a retreat offers the deep refill you need. It's not self-indulgent — 
                  it's essential maintenance.
                </p>
              </div>
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-3">You're at a Crossroads</h3>
                <p className="text-neutral-600 text-sm">
                  Major life transitions — career changes, relationship shifts, health challenges — 
                  benefit from the clarity and perspective a retreat provides. Space to process and 
                  decide.
                </p>
              </div>
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-3">You Want to Deepen Your Practice</h3>
                <p className="text-neutral-600 text-sm">
                  Whether you're a beginner wanting immersion or an experienced yogi seeking depth, 
                  retreats accelerate learning and understanding in ways regular classes can't match.
                </p>
              </div>
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-3">You Crave Connection</h3>
                <p className="text-neutral-600 text-sm">
                  If you feel isolated or disconnected, retreats offer genuine human connection 
                  with like-minded people. The bonds formed over shared practice often surprise 
                  people with their depth.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lasting Effects */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Benefits That Last Beyond the Retreat
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                One of the most remarkable things about yoga retreats is how the benefits 
                <strong> extend far beyond the retreat itself</strong>. Guests often report:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span>Maintaining a regular yoga or meditation practice for months afterwards</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span>Making positive lifestyle changes — better sleep habits, healthier eating, less screen time</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span>Improved relationships through better stress management and presence</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span>Greater self-awareness and ability to recognise when they need rest</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                  <span>A lasting sense of what's truly important versus what's just noise</span>
                </li>
              </ul>
              <p>
                Many people describe their first yoga retreat as a turning point — the beginning 
                of a new way of relating to themselves and their lives.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Experience These Benefits?
            </h2>
            <p className="text-neutral-600 mb-6">
              Explore our upcoming yoga retreats and discover which experience is right for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View Yoga Retreats UK <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Ask a Question
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Related Guides</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
              <p className="text-neutral-500 text-xs mt-1">First retreat guide</p>
            </Link>
            <Link to="/retreats/packing-list" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Packing List</span>
              <p className="text-neutral-500 text-xs mt-1">What to bring</p>
            </Link>
            <Link to="/retreats/beginners" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Beginner's Guide</span>
              <p className="text-neutral-500 text-xs mt-1">New to yoga?</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BenefitsOfYogaRetreats;
